/* Copyright (c) 2021 MOCA Systems, Inc.  All rights reserved. */

import { Injectable } from '@angular/core';
import { Observable, combineLatest, Subject, EMPTY, of, BehaviorSubject } from 'rxjs';
import { map, distinctUntilChanged, takeUntil, switchMap, filter } from 'rxjs/operators';
import {
	Ticket,
	TicketFields,
	ProjectMember,
	PlanAccessLevel,
	isProjectTypeDisabled,
} from '@touchplan/common';
import { ProjectLegacy } from '../app-data/models/Project';
import { PlanAccess } from '../app-data/models/PlanAccess';
import { hasAccessToTicket, hasRestrictedAccess, isProjectMemberAdmin } from './plan-access-check-utils';
import { subjectSubscribeWithoutComplete } from '../plain-javascript/services/rxjs-internal-extensions';

/**
 * This class sets up and configures observables for plan access, project, and authenticated project member, and provides
 * mechanisms to check restricted access, admin status, and access levels. The `start` method is used to initialize these
 * observables and manage their subscriptions.
 *
 * @see start
 */
@Injectable({
	providedIn: 'root',
})
export class PlanAccessCheck {
	private planAccess$: BehaviorSubject<PlanAccess>;
	private project$: BehaviorSubject<ProjectLegacy>;
	private authedProjectMember$: BehaviorSubject<ProjectMember>;

	private hasRestrictedAccessSubject$ = new BehaviorSubject<boolean>(null);
	private isAdminSubject$ = new BehaviorSubject<boolean>(null);
	private accessLevelSubject$ = new BehaviorSubject<PlanAccessLevel>(null);

	private shutdownSubject$ = new Subject<void>();

	/**
	 * Initializes and sets up observables for plan access, project, and authenticated project member,
	 * and configures subjects for restricted access, admin status, and access level. This is done to "create" the necessary
	 * context for the methods of this class to work.
	 *
	 * @param {Observable<PlanAccess>} planAccess$ - An observable emitting plan access information.
	 * @param {Observable<ProjectLegacy>} project$ - An observable emitting project information.
	 * @param {Observable<ProjectMember>} authedProjectMember$ - An observable emitting the authenticated project member.
	 */
	public start(
		planAccess$: Observable<PlanAccess>,
		project$: Observable<ProjectLegacy>,
		authedProjectMember$: Observable<ProjectMember>
	) {
		this.planAccess$ = new BehaviorSubject(null);
		planAccess$.pipe(takeUntil(this.shutdownSubject$)).subscribe(this.planAccess$);

		this.project$ = new BehaviorSubject(null);
		project$.pipe(takeUntil(this.shutdownSubject$)).subscribe(this.project$);

		this.authedProjectMember$ = new BehaviorSubject(null);
		authedProjectMember$.pipe(takeUntil(this.shutdownSubject$)).subscribe(this.authedProjectMember$);

		// hasRestrictedAccessSubject$
		combineLatest([this.planAccess$, this.project$, this.authedProjectMember$])
			.pipe(
				map(([planAccess, project, authedProjectMember]) =>
					hasRestrictedAccess(project, planAccess, authedProjectMember)
				),
				distinctUntilChanged(),
				takeUntil(this.shutdownSubject$)
			)
			.subscribe(subjectSubscribeWithoutComplete(this.hasRestrictedAccessSubject$));

		// isAdminSubject$
		this.authedProjectMember$
			.pipe(
				map((authedProjectMember) => isProjectMemberAdmin(authedProjectMember)),
				takeUntil(this.shutdownSubject$)
			)
			.subscribe(subjectSubscribeWithoutComplete(this.isAdminSubject$));

		// accessLevelSubject$
		this.planAccess$
			.pipe(
				// an undefined planAccess should be treated as live for legacy reasons
				map((planAccess) => (planAccess ? planAccess.view.accessLevel : PlanAccessLevel.live)),
				takeUntil(this.shutdownSubject$)
			)
			.subscribe(subjectSubscribeWithoutComplete(this.accessLevelSubject$));
	}

	/**
	 * Observable that emits the current restricted access status.
	 *
	 * This property uses the `hasRestrictedAccess` method (defined in another file) behind the scenes
	 * to determine if access to the plan is restricted, based on the project, plan access, and
	 * authenticated project member.
	 *
	 * @see hasRestrictedAccess
	 * @type {Observable<boolean>}
	 */
	public hasRestrictedAccess$ = this.hasRestrictedAccessSubject$.asObservable();

	// BEGIN PLACEHOLDER REPLACE AS PART OF TP-6401
	/**
	 * Synchronously retrieves the current restricted access status.
	 *
	 * This getter uses the `hasRestrictedAccess` method (defined in another file) behind the scenes
	 * to determine if access to the plan is restricted, based on the project, plan access, and
	 * authenticated project member.
	 *
	 * @see hasRestrictedAccess
	 * @returns {boolean} - The current restricted access status.
	 */
	public get hasRestrictedAccessSync() {
		return this.hasRestrictedAccessSubject$.getValue();
	}
	// END PLACEHOLDER REPLACE AS PART OF TP-6401

	/**
	 * Observable that emits the current admin status of the authenticated project member.
	 *
	 * This property uses the `isProjectMemberAdmin` method (defined in another file) behind the scenes
	 * to determine if the authenticated project member has an admin access level.
	 *
	 * @see isProjectMemberAdmin
	 * @type {Observable<boolean>}
	 */
	public isAdmin$ = this.isAdminSubject$.asObservable();

	// BEGIN PLACEHOLDER REPLACE AS PART OF TP-6401
	/**
	 * Synchronously retrieves the current admin status of the authenticated project member.
	 *
	 * This getter uses the `isProjectMemberAdmin` method (defined in another file) behind the scenes
	 * to determine if the authenticated project member has an admin access level.
	 *
	 * @see isProjectMemberAdmin
	 * @returns {boolean} - The current admin status of the authenticated project member.
	 */
	public get isAdminSync() {
		return this.isAdminSubject$.getValue();
	}
	// END PLACEHOLDER REPLACE AS PART OF TP-6401

	/**
	 * Observable that emits the current access level of the plan.
	 *
	 * This property emits the current access level of the plan, based on the plan access view.
	 *
	 * @type {Observable<PlanAccessLevel>}
	 */
	public accessLevel$ = this.accessLevelSubject$.asObservable();

	// BEGIN PLACEHOLDER REPLACE AS PART OF TP-6401
	/**
	 * Synchronously retrieves the current access level of the plan.
	 *
	 * @see accessLevel$
	 * @returns {PlanAccessLevel} - The current restricted access status.
	 */
	public get accessLevelSync() {
		return this.accessLevelSubject$.getValue();
	}
	// END PLACEHOLDER REPLACE AS PART OF TP-6401

	/**
	 * Returns an observable that emits a boolean indicating whether the authenticated project member
	 * has access to the provided ticket.
	 *
	 * This method uses the `hasAccessToTicket` method (defined in another file) behind the scenes to determine
	 * if the authenticated project member has access to the provided ticket.
	 *
	 * @see hasAccessToTicket
	 * @param {Observable<Ticket | TicketFields>} ticket$ - An observable emitting the ticket or ticket fields.
	 * @returns {Observable<boolean>} - An observable emitting `true` if the authenticated project member has access to the ticket,
	 * or `false` otherwise. Emits `EMPTY` if the ticket is `null`.
	 */
	public hasAccessToTicket$(ticket$: Observable<Ticket | TicketFields>) {
		// it is possible that the ticket$ returns null value. See edit-wrapper-component where its hasAcce$$ field is initialized
		return combineLatest([ticket$, this.authedProjectMember$]).pipe(
			switchMap(([ticket, authedProjectMember]) => {
				if (ticket) {
					return of(hasAccessToTicket(ticket, authedProjectMember));
				} else {
					return EMPTY;
				}
			})
		);
	}

	// BEGIN PLACEHOLDER REPLACE AS PART OF TP-6401
	/**
	 * Synchronously retrieves the current access status of the authenticated project member for the provided ticket.
	 *
	 * This method uses the `hasAccessToTicket` method (defined in another file) behind the scenes to determine
	 * if the authenticated project member has access to the provided ticket.
	 *
	 * @see hasAccessToTicket
	 * @param {Ticket | TicketFields} ticket - The ticket for which access is being evaluated.
	 * @returns {boolean} - The current access status of the provided data.
	 */
	public hasAccessToTicketSync(ticket: Ticket | TicketFields | { data: TicketFields }) {
		const authedProjectMember = this.authedProjectMember$.getValue();
		return hasAccessToTicket(ticket, authedProjectMember);
	}
	// END PLACEHOLDER REPLACE AS PART OF TP-6401

	// BEGIN PLACEHOLDER REPLACE AS PART OF TP-6401
	/**
	 * Checks if the authenticated project member is either an admin or matches the specified project member ID.
	 *
	 * This method uses the `isProjectMemberAdmin` function (defined in another file) to determine if the authenticated
	 * project member has an admin access level. Additionally, it checks if the authenticated member's ID matches the provided
	 * project member ID (`pmId`).
	 *
	 * @see isProjectMemberAdmin
	 * @param {string} pmId - The project member ID to compare against the authenticated project member's ID.
	 * @returns {boolean} - Returns `true` if admin or pmId matches.
	 * Returns `false` otherwise.
	 */
	public pmOrAdminSync(pmId: string) {
		const authedProjectMember = this.authedProjectMember$.getValue();
		return isProjectMemberAdmin(authedProjectMember) || authedProjectMember.view.projectMemberId === pmId;
	}
	// END PLACEHOLDER REPLACE AS PART OF TP-6401

	// BEGIN PLACEHOLDER REPLACE AS PART OF TP-6401
	/**
	 * Checks if the authenticated project member has the specified role.
	 *
	 * This method verifies if the authenticated project member's primary role or any of their secondary roles match the given `roleId`.
	 * It retrieves the authenticated project member's roles and checks them against the provided role ID.
	 *
	 * @param {string} roleId - The role ID to check against the authenticated project member's roles.
	 * @returns {boolean} - Returns `true` if there is a match. Returns `false` otherwise.
	 */
	public hasRoleSync(roleId: string) {
		const authedProjectMember = this.authedProjectMember$.getValue();
		if (authedProjectMember.view.roleId === roleId) {
			return true;
		}
		if (!!authedProjectMember.view.secondaryRoles) {
			for (const key in authedProjectMember.view.secondaryRoles) {
				if (authedProjectMember.view.secondaryRoles[key] === roleId) {
					return true;
				}
			}
		}
		return false;
	}
	// END PLACEHOLDER REPLACE AS PART OF TP-6401

	// BEGIN PLACEHOLDER REPLACE AS PART OF TP-6401
	/**
	 * Checks if the authenticated project member's ID matches the specified project member ID.
	 *
	 * This method compares the ID of the authenticated project member with the provided `pmId` to determine if they match.
	 *
	 * @param {string} pmId - The project member ID to compare against the authenticated project member's ID.
	 * @returns {boolean} - Returns `true` if the authenticated project member's ID matches `pmId`.
	 * Returns `false` otherwise.
	 */
	public pmMatchesSync(pmId: string) {
		const authedProjectMember = this.authedProjectMember$.getValue();
		return authedProjectMember.view.projectMemberId === pmId;
	}
	// END PLACEHOLDER REPLACE AS PART OF TP-6401

	/**
	 * Returns an observable that emits a boolean indicating whether the ticket is editable.
	 *
	 * The ticket is considered editable if the authenticated project member
	 * has access to the ticket and the access is not restricted (i.e., `isReadOnly` is `false`).
	 *
	 * @param {Observable<Ticket>} ticket$ - An observable emitting the ticket to check for editability.
	 * @returns {Observable<boolean>} - An observable emitting `true` if the ticket is editable, Emits `false` otherwise.
	 */
	public getTicketIsEditable$(ticket$: Observable<Ticket>): Observable<boolean> {
		return combineLatest([this.hasAccessToTicket$(ticket$), this.hasRestrictedAccess$]).pipe(
			map(([hasAccessToTicket, isReadOnly]) => hasAccessToTicket && !isReadOnly)
		);
	}

	// BEGIN PLACEHOLDER REPLACE AS PART OF TP-6401
	public isProjectDisabledSync() {
		return isProjectTypeDisabled(this.project$.getValue().projectType);
	}
	// END PLACEHOLDER REPLACE AS PART OF TP-6401

	public destroy() {
		this.planAccess$.complete();
		this.project$.complete();
		this.authedProjectMember$.complete();
		this.shutdownSubject$.next();
	}
}

// - provides access to pm data
// - isReadOnly check
//   - project _isProjectTypeDisabled
//   - project is sampleProjectShared
//   - planAccess planAccess/$projectId/$plan/accessLevel
//     - locked
//     - archived
//     - live
//   - planAccess planAccess/$projectId/$plan/$pmId/readOnlyMode
// - isAdmin pm data check
// - hasRole check
// - pmMatch check
// - ticketAccess check
